import { useState } from "react";
import { Link } from "react-router";

import { ROUTES, routeTo } from "@/app/routes";

import {
  formatBps,
  formatCount,
  formatCountOf,
  formatDay,
  formatDayLong,
  formatPaise,
  todayIn,
} from "@/core/format/format";
import { useBrandList } from "@/features/brands/brands.queries";
import { useSubscriptionList } from "@/features/subscriptions/subscriptions.queries";
import { Card, CardHeader } from "@/ui/card";
import { cn } from "@/ui/cn";
import { SelectField } from "@/ui/field";
import { Segmented } from "@/ui/segmented";
import { Skeleton } from "@/ui/spinner";
import { HelpTip } from "@/ui/help-tip";
import { Stat } from "@/ui/stat";
import { ErrorState } from "@/ui/states";

import {
  useAnalyticsPlans,
  useAnalyticsSummary,
  type AnalyticsPlan,
  type AnalyticsSummary,
  type RevenuePeriod,
  type SignupRange,
} from "./analytics.queries";
import {
  formatMonth,
  latestRetention,
  RETENTION_HELP,
  retentionWords,
  signupBars,
  SIGNUP_RANGES,
} from "./overview-display";

/** The old admin's revenue periods. */
const PERIODS: readonly { value: RevenuePeriod; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "7d", label: "7 days" },
  { value: "14d", label: "14 days" },
  { value: "1m", label: "1 month" },
  { value: "3m", label: "3 months" },
  { value: "6m", label: "6 months" },
  { value: "1y", label: "1 year" },
  { value: "all", label: "All time" },
];

function KeyNumbers({ summary }: { summary: AnalyticsSummary }) {
  const retention = latestRetention(summary.retention);
  const newPlans = summary.thisMonth.signups;
  const period = PERIODS.find((p) => p.value === summary.period.period)!.label;
  return (
    <section
      aria-label="Business numbers"
      className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 2xl:grid-cols-6"
    >
      <Stat
        label="Total subscribers"
        value={formatCount(summary.customers)}
        detail={`${formatCount(summary.activeSubscribers)} active now · ${formatCountOf(summary.subscriptions, "plan")} sold`}
      />
      <Stat
        label={`Revenue · ${period}`}
        value={formatPaise(summary.period.revenuePaise)}
        detail={`${formatCountOf(summary.period.signups, "new plan")} · all time ${formatPaise(summary.revenuePaise)}`}
      />
      <Stat
        label="Revenue this month"
        value={formatPaise(summary.thisMonth.revenuePaise)}
        detail={formatMonth(summary.month)}
      />
      <Stat label="New plans" value={formatCount(newPlans)} detail="This month" />
      <Stat
        label="Cancellation rate"
        value={
          newPlans > 0 ? formatBps(Math.round((summary.thisMonth.cancellations / newPlans) * 10_000)) : "–"
        }
        detail={`${formatCount(summary.thisMonth.cancellations)} cancelled this month`}
      />
      <Stat
        label={
          <>
            Retention
            <HelpTip topic="Retention">{RETENTION_HELP}</HelpTip>
          </>
        }
        value={retention ? formatBps(retention.retentionBps!) : "–"}
        detail={
          retention ? (
            <>
              {retentionWords(retention).outcome}
              <span className="block text-muted">
                Churn {formatBps(10_000 - retention.retentionBps!)} · plans that ended in{" "}
                {formatMonth(retention.month, "name")}
                {retentionWords(retention).pending && ` · ${retentionWords(retention).pending}`}
              </span>
            </>
          ) : (
            "No plans have finished their 14 days yet"
          )
        }
      />
    </section>
  );
}

function SignupsCard({
  summary,
  range,
  onRange,
}: {
  summary: AnalyticsSummary;
  range: SignupRange;
  onRange: (range: SignupRange) => void;
}) {
  const today = todayIn("Asia/Kolkata");
  const bars = signupBars(range, summary.signups.points, today);
  const top = Math.max(1, ...bars.map((b) => b.count));
  const total = bars.reduce((sum, b) => sum + b.count, 0);
  const byMonth = range === "6m" || range === "1y";
  const label = (bucket: string) => (byMonth ? formatMonth(bucket) : formatDayLong(bucket));
  return (
    <Card aria-labelledby="signups" className="flex flex-col gap-4">
      <CardHeader id="signups" title="New subscriptions" aside={formatCount(total)} className="mb-0" />
      <Segmented
        label="Period"
        options={SIGNUP_RANGES.map((r) => ({ value: r.value, label: r.label }))}
        value={range}
        onChange={onRange}
      />
      <div role="img" aria-label={`${formatCountOf(total, "new plan")} in the period`}>
        <div className="flex h-36 items-end gap-[3px]">
          {bars.map((b) => (
            <span
              key={b.bucket}
              title={`${label(b.bucket)}: ${formatCount(b.count)}`}
              className={cn(
                "min-w-0 flex-1 rounded-t-[4px]",
                b.count > 0 ? "bg-primary" : "bg-surface-muted",
              )}
              style={{ height: `${Math.max(4, (b.count / top) * 100)}%` }}
            />
          ))}
        </div>
      </div>
    </Card>
  );
}

function RetentionCard({ summary }: { summary: AnalyticsSummary }) {
  return (
    <Card aria-labelledby="retention" className="flex flex-col gap-3">
      <CardHeader id="retention" title="Retention by month" className="mb-0" />
      <p className="text-[13px] text-muted">
        Of the customers whose plan ended in a month, how many bought another plan from the same brand within
        14 days. A plan still inside its 14 days counts once they're over.
      </p>
      {summary.retention.every((r) => r.ended === 0) ? (
        <p className="text-sm text-muted">No plans have ended in the last 6 months.</p>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {summary.retention.map((r) => (
            <li key={r.month} className="grid grid-cols-[5.5rem_minmax(0,1fr)_auto] items-center gap-3">
              <span className="text-sm font-semibold">{formatMonth(r.month, "name")}</span>
              <span className="h-2.5 overflow-hidden rounded-full bg-surface-muted">
                <span
                  className="block h-full rounded-full bg-primary"
                  style={{ width: `${(r.retentionBps ?? 0) / 100}%` }}
                />
              </span>
              <span className="tabular text-sm">
                {r.ended === 0 ? (
                  <span className="text-muted">no plans ended</span>
                ) : (
                  <>
                    {r.retentionBps !== null && (
                      <span className="font-bold">{formatBps(r.retentionBps)} · </span>
                    )}
                    <span className="text-muted">
                      {r.retentionBps !== null &&
                        `${formatCount(r.renewed)} of ${formatCount(r.ended - r.pending)} bought again`}
                      {r.retentionBps !== null && r.pending > 0 && " · "}
                      {r.pending > 0 && `${formatCount(r.pending)} can still renew`}
                    </span>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

const PLAN_GRID = "xl:grid xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_5rem_5rem_8rem_8rem_5rem]";

function PlanRow({ plan }: { plan: AnalyticsPlan }) {
  const cells: [string, string][] = [
    ["Brand", plan.brandName],
    ["Active", formatCount(plan.activeSubscribers)],
    ["Sold", formatCount(plan.subscriptions)],
    ["Revenue", formatPaise(plan.revenuePaise)],
    ["This month", formatPaise(plan.revenueThisMonthPaise)],
    ["Churn", plan.churnBps === null ? "–" : formatBps(plan.churnBps)],
  ];
  return (
    <li
      className={cn(
        PLAN_GRID,
        "flex flex-col gap-1.5 border-t border-border-subtle px-4 py-3.5 xl:items-center xl:gap-4 xl:px-5",
      )}
    >
      <span className="font-bold">{plan.planName}</span>
      {cells.map(([label, value]) => (
        <span key={label} className="tabular flex justify-between text-sm xl:block">
          <span className="text-muted xl:hidden">{label}</span>
          <span className={label === "Brand" ? "text-ink-2" : "font-semibold"}>{value}</span>
        </span>
      ))}
    </li>
  );
}

/** The latest plans bought or given, across brands or for one (the old admin's Recent Activity). */
function RecentActivity({ brandId }: { brandId: string }) {
  const list = useSubscriptionList(brandId ? { brandId } : {});
  const recent = (list.data?.pages[0]?.data ?? []).slice(0, 8);
  return (
    <Card aria-labelledby="recent" className="flex flex-col gap-3">
      <CardHeader
        id="recent"
        title="Recent activity"
        className="mb-0"
        aside={
          <Link
            to={brandId ? `${ROUTES.subscriptions}?brandId=${brandId}` : ROUTES.subscriptions}
            className="font-bold no-underline"
          >
            All subscriptions
          </Link>
        }
      />
      {list.isPending ? (
        <Skeleton className="h-40 rounded-xl" />
      ) : recent.length === 0 ? (
        <p className="text-sm text-muted">No plans bought yet.</p>
      ) : (
        <ul className="flex flex-col">
          {recent.map((s) => (
            <li key={s.id} className="border-t border-border-subtle first:border-t-0">
              <Link
                to={routeTo.subscription(s.id)}
                className="flex items-center gap-3 py-2.5 text-ink no-underline hover:bg-surface-muted"
              >
                <span className="flex min-w-0 grow flex-col gap-0.5">
                  <span className="truncate text-sm font-semibold">
                    {s.customer.name ?? "Name not given"}
                  </span>
                  <span className="truncate text-[13px] text-muted">
                    {s.planName} · {s.brandName} · {formatDay(s.createdAt.slice(0, 10))}
                  </span>
                </span>
                <span className="tabular text-sm font-bold">
                  {s.source === "ADMIN" ? "Free" : formatPaise(s.pricePaise)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** Top plans across brands (read-only: prices and menus are the brand's). */
function PlansCard({ brandId }: { brandId: string }) {
  const plans = useAnalyticsPlans(brandId);
  return (
    <Card aria-labelledby="plans" className="overflow-hidden p-0">
      <CardHeader id="plans" title="Plans" aside="Churn: last 3 months" className="mb-0 px-5 pt-5 pb-3" />
      {plans.isPending ? (
        <div className="p-5">
          <Skeleton className="h-40 rounded-xl" />
        </div>
      ) : plans.isError ? (
        <div className="p-5">
          <ErrorState error={plans.error} onRetry={() => void plans.refetch()} />
        </div>
      ) : plans.data.length === 0 ? (
        <p className="px-5 pb-5 text-sm text-muted">No plans yet.</p>
      ) : (
        <>
          <div
            aria-hidden="true"
            className={cn(
              PLAN_GRID,
              "hidden gap-4 bg-surface-muted px-5 py-2.5 text-xs font-bold tracking-wide text-muted uppercase",
            )}
          >
            {["Plan", "Brand", "Active", "Sold", "Revenue", "This month", "Churn"].map((c) => (
              <span key={c}>{c}</span>
            ))}
          </div>
          <ul aria-label="Plans" className="[&>li:first-child]:border-t-0 xl:[&>li:first-child]:border-t">
            {plans.data.map((p) => (
              <PlanRow key={p.planId} plan={p} />
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}

/**
 * The business over time (v1's admin Overview: revenue, monthly revenue, churn, cancellation rate, top plans),
 * for every brand or one.
 */
export function BusinessNumbers() {
  const [brandId, setBrandId] = useState("");
  const [range, setRange] = useState<SignupRange>("1m");
  const [period, setPeriod] = useState<RevenuePeriod>("all");
  const brands = useBrandList({});
  const summary = useAnalyticsSummary(brandId, range, period);
  const brandOptions = brands.data?.pages.flatMap((p) => p.data) ?? [];
  return (
    <section aria-labelledby="business" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 id="business" className="text-xl font-extrabold tracking-tight">
          Business
        </h2>
        <div className="flex flex-wrap gap-3">
          <SelectField
            label="Brand"
            value={brandId}
            onChange={(event) => setBrandId(event.target.value)}
            className="h-11 w-56"
          >
            <option value="">Every brand</option>
            {brandOptions.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </SelectField>
          <SelectField
            label="Revenue over"
            value={period}
            onChange={(event) => setPeriod(event.target.value as RevenuePeriod)}
            className="h-11 w-40"
          >
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </SelectField>
        </div>
      </div>
      {summary.isPending ? (
        <Skeleton className="h-[126px] rounded-2xl" />
      ) : summary.isError ? (
        <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
      ) : (
        <>
          <KeyNumbers summary={summary.data} />
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <SignupsCard summary={summary.data} range={range} onRange={setRange} />
            <RetentionCard summary={summary.data} />
          </div>
        </>
      )}
      {/* Full width each: the plans table needs the room. */}
      <PlansCard brandId={brandId} />
      <RecentActivity brandId={brandId} />
    </section>
  );
}
